import { useState, useCallback, useEffect, useRef } from 'react';
import { Globe, ExternalLink, Copy, Check } from 'lucide-react';
import type { ToolComponentProps } from './index';

interface DNSRecord {
  data: string;
  ttl: number;
}

interface DNSResult {
  domain: string;
  timestamp: string;
  records: Record<string, DNSRecord[]>;
}

// In production, nginx serves both frontend and API from same origin
const API_BASE = '';

/**
 * Extract domain from input - handles both plain domains and full URLs
 */
function extractDomain(input: string): string {
  const trimmed = input.trim();
  
  // If it looks like a URL (has protocol or starts with www.)
  if (trimmed.includes('://') || trimmed.startsWith('www.')) {
    try {
      // Add protocol if missing for URL parsing
      const urlString = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
      const url = new URL(urlString);
      return url.hostname;
    } catch {
      // If URL parsing fails, return as-is
      return trimmed;
    }
  }
  
  return trimmed;
}

/**
 * Check if an IP address is a public (routable) IP
 */
function isPublicIP(ip: string): boolean {
  // IPv4 check
  const ipv4Parts = ip.split('.').map(Number);
  if (ipv4Parts.length === 4 && ipv4Parts.every(p => !isNaN(p) && p >= 0 && p <= 255)) {
    const [a, b] = ipv4Parts;
    // Private ranges
    if (a === 10) return false; // 10.0.0.0/8
    if (a === 172 && b >= 16 && b <= 31) return false; // 172.16.0.0/12
    if (a === 192 && b === 168) return false; // 192.168.0.0/16
    // Loopback
    if (a === 127) return false; // 127.0.0.0/8
    // Link-local
    if (a === 169 && b === 254) return false; // 169.254.0.0/16
    // Reserved
    if (a === 0) return false; // 0.0.0.0/8
    if (a >= 224) return false; // Multicast & reserved (224.0.0.0+)
    return true;
  }
  
  // IPv6 - check for link-local, loopback, private
  const ipLower = ip.toLowerCase();
  if (ipLower === '::1') return false; // Loopback
  if (ipLower.startsWith('fe80:')) return false; // Link-local
  if (ipLower.startsWith('fc') || ipLower.startsWith('fd')) return false; // Unique local
  if (ipLower.startsWith('ff')) return false; // Multicast
  
  return true;
}

export function DNSLookup({ initialValue }: ToolComponentProps) {
  const [input, setInput] = useState(initialValue || '');
  const [result, setResult] = useState<DNSResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const hasAutoTriggered = useRef(false);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  const handleLookup = useCallback(async () => {
    const rawInput = input.trim();
    if (!rawInput) {
      setError('Please enter a domain name or URL');
      return;
    }

    // Extract domain from URL if needed
    const domain = extractDomain(rawInput);

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/dns/${encodeURIComponent(domain)}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'DNS lookup failed');
        return;
      }

      setResult(data);

      // Update browser URL for sharing
      const params = new URLSearchParams();
      params.set('host', domain);
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    } catch (err) {
      setError('Failed to connect to the server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [input]);

  // Auto-trigger lookup when initialValue is provided via URL
  useEffect(() => {
    if (initialValue && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      handleLookup();
    }
  }, [initialValue, handleLookup]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLookup();
    }
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400 p-4 bg-red-500/10 rounded-lg">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!result) {
      return (
        <div className="text-[var(--text-muted)] text-center py-8">
          Enter a domain or URL above and click "Lookup" to query DNS records.
        </div>
      );
    }

    const recordTypes = Object.keys(result.records);

    if (recordTypes.length === 0) {
      return (
        <div className="text-[var(--text-muted)]">
          No DNS records found for this domain.
        </div>
      );
    }

    // Find primary public IPv4 address
    const aRecords = result.records['A'] || [];
    const publicIPv4 = aRecords.find(r => isPublicIP(r.data))?.data;

    return (
      <div className="space-y-6">
        {/* Primary Public IP - Prominent Display */}
        {publicIPv4 && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
            <div className="text-xs text-green-400 mb-1 font-medium">Public IPv4 Address</div>
            <div className="flex items-center gap-3">
              <span className="text-2xl font-mono font-bold text-green-400">{publicIPv4}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(publicIPv4)}
                className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
        )}

        <div className="text-xs text-[var(--text-muted)]">
          Query for <span className="text-[var(--accent)]">{result.domain}</span> at{' '}
          {new Date(result.timestamp).toLocaleString()}
        </div>

        {['A', 'AAAA', 'CNAME', 'MX', 'NS', 'TXT'].map((type) => {
          const records = result.records[type];
          if (!records || records.length === 0) return null;

          const isIPRecord = type === 'A' || type === 'AAAA';

          return (
            <div key={type}>
              <h3 className="text-[var(--accent)] font-semibold mb-2 flex items-center gap-2">
                <span>{type}</span>
                <span className="text-xs text-[var(--text-muted)]">
                  {records.length} record{records.length !== 1 ? 's' : ''}
                </span>
              </h3>
              <div className="space-y-1">
                {records.map((record, index) => {
                  const isPublic = isIPRecord && isPublicIP(record.data);
                  
                  return (
                    <div
                      key={index}
                      className="flex items-start gap-4 text-sm font-mono"
                    >
                      <span className={`break-all flex items-center gap-2 ${isPublic ? 'text-green-400 font-semibold' : ''}`}>
                        {record.data}
                        {isPublic && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-500/20 text-green-400 font-normal">
                            <ExternalLink className="w-3 h-3" />
                            Public
                          </span>
                        )}
                      </span>
                      <span className="text-[var(--text-muted)] whitespace-nowrap">
                        TTL: {record.ttl}s
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" onKeyDown={handleKeyDown}>
      {/* Input Section */}
      <div className="space-y-2">
        <label className="block text-xs sm:text-sm font-medium text-[var(--text-primary)]">
          Domain or URL
        </label>
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <input
            type="text"
            className="flex-1 min-w-0 px-3 sm:px-4 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent font-mono text-sm"
            placeholder="example.com or https://example.com/path"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck={false}
          />
          <button
            type="button"
            onClick={handleLookup}
            disabled={isLoading || !input.trim()}
            className="btn-primary flex items-center justify-center gap-2 px-4 sm:px-6 whitespace-nowrap"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Looking up...
              </>
            ) : (
              <>
                <Globe className="w-4 h-4" />
                Lookup
              </>
            )}
          </button>
        </div>
      </div>

      {/* Output Section */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-[var(--text-primary)]">
          DNS Records
        </label>
        <div className="output-panel">
          {renderOutput()}
        </div>
      </div>

      <div className="text-xs text-[var(--text-muted)]">
        <span className="text-[var(--accent)]">Tip:</span> You can paste full URLs - the domain will be extracted automatically. Public IPs are highlighted in green.
      </div>
    </div>
  );
}
